import { IAppContext } from 'App/AppContext';
import { IHubConnection, startHubConnection } from './GeneralHub';

export enum ProgressHubProcess {
  None = 0,
  PublishLibraryItems = 1,
  ControlEvidenceAudit = 2,
  ControlDocumentationAudit = 3,
  TaskEvidenceAudit = 4,
  AuditFindings = 5,
  AutomationDrafting = 6,
  //the KPI data report job (PLAN-064): entity type 0, the job id; entityId arrives as a string
  KpiDataReport = 7,
  //the two supplier investigations (PLAN-073): entity type Asset, the asset id
  SupplierIncidents = 8,
  SupplierCompliance = 9,
  //the evidence audit of one entity (PLAN-077): one step per judged chunk of criteria
  EntityEvidenceAudit = 10,
}

export enum ProgressHubStatus {
  Initializing = 0,
  InProgress = 1,
  Finishing = 2,
  Completed = 3,
  Failed = 9,
}

// What a count counts, for a process with more than one phase (the KPI report: its data rows, then the report's steps);
// None for every other process. Mirrors ProgressHubPhase of the API by hand, like the status (PLAN-076).
export enum ProgressHubPhase {
  None = 0,
  Data = 1,
  Report = 2,
}

export class ProgressUpdate {
  process: ProgressHubProcess;

  status: ProgressHubStatus;

  percentage?: number;

  totalItems?: number;

  processedItems?: number;

  entityId?: string;

  // Current-step label for long-running processes (control audits); undefined for others.
  message?: string;

  phase?: ProgressHubPhase;

  constructor() {
    this.process = ProgressHubProcess.None;
    this.status = ProgressHubStatus.Initializing;
  }
}

export const ReceiveProgressUpdate = 'ReceiveProgressUpdate';

const SubscribeToProcess = 'SubscribeToProcess';

const UnsubscribeFromProcess = 'UnsubscribeFromProcess';

export type TaskHubMethodReceiveProgress = (progressUpdate: ProgressUpdate) => void;

export const ProgressHubEndpoint = 'hubs/progress';

export const startProgressHubConnection = async (
  appContext: IAppContext,
  progressCallback: TaskHubMethodReceiveProgress,
): Promise<IHubConnection | null> => {
  return startHubConnection(appContext, ProgressHubEndpoint, [
    { method: ReceiveProgressUpdate, callback: progressCallback },
  ]);
};

// Join a process-identity group so this connection receives live progress for a long-running run,
// independent of the connection id. Re-invoke after every reconnect (group membership is lost).
export const subscribeToProcess = async (
  hub: IHubConnection | null,
  process: ProgressHubProcess,
  entityType: number,
  entityId: number,
): Promise<void> => {
  if (!hub) {
    return;
  }
  try {
    await hub.connection.invoke(SubscribeToProcess, process, entityType, entityId);
  } catch {
    // Best-effort; the client falls back to the persisted snapshot for catch-up.
  }
};

export const unsubscribeFromProcess = async (
  hub: IHubConnection | null,
  process: ProgressHubProcess,
  entityType: number,
  entityId: number,
): Promise<void> => {
  if (!hub) {
    return;
  }
  try {
    await hub.connection.invoke(UnsubscribeFromProcess, process, entityType, entityId);
  } catch {
    // Best-effort.
  }
};
